import {Component, DestroyRef, inject, Input, OnChanges, OnInit, SimpleChanges} from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NG_VALIDATORS,
  NG_VALUE_ACCESSOR,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validator,
  ValidatorFn,
} from '@angular/forms';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {DateTime} from 'luxon';
import {TodayCalendarHeaderComponent} from '../today-calendar-header/today-calendar-header.component';

@Component({
  selector: 'app-datetime-picker-v2',
  templateUrl: './datetime-picker-v2.component.html',
  styleUrls: ['./datetime-picker-v2.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: DatetimePickerV2Component,
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: DatetimePickerV2Component,
      multi: true,
    },
  ],
  standalone: false,
})
export class DatetimePickerV2Component implements ControlValueAccessor, Validator, OnInit, OnChanges {
  @Input() defaultDate: string | Date | DateTime | null = null;
  @Input() dateOnly = false;
  @Input() timeOnly = false;
  @Input() clearable = false;
  @Input() disabled = false;
  @Input() minuteStep = 5;
  @Input() min: DateTime | null = null;
  @Input() max: DateTime | null = null;
  @Input() validators: ValidatorFn[] = [];

  readonly calendarHeader = TodayCalendarHeaderComponent;

  get interval(): string {
    return `${this.minuteStep || 5}m`;
  }

  get hasValue(): boolean {
    return !!(this.form.get('date').value || this.form.get('time').value);
  }

  get startAt(): DateTime | null {
    const current = this.form.get('date').value;
    if (current) {
      return current;
    }
    try {
      return this.parse(this.defaultDate);
    } catch {
      return null;
    }
  }

  clear(): void {
    this.form.setValue({date: null, time: null});
    this.form.markAsDirty();
    this.form.markAsTouched();
    this.onTouched();
  }

  form = new UntypedFormGroup({
    date: new UntypedFormControl(null),
    time: new UntypedFormControl(null),
  });

  private destroyRef = inject(DestroyRef);
  private onChange: (value: string | null) => void = () => {};
  private onTouched: () => void = () => {};
  private onValidatorChange: () => void = () => {};
  private initialized = false;
  private lastParentControl: AbstractControl | null = null;

  ngOnInit(): void {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.emit());
    this.form.statusChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.onValidatorChange());
    this.initialized = true;
    this.applyInitialValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.defaultDate && !changes.defaultDate.firstChange) {
      this.applyInitialValue();
    }
    if (changes.disabled) {
      this.applyDisabledState();
    }
    if (changes.validators && !changes.validators.firstChange) {
      this.onValidatorChange();
    }
  }

  writeValue(value: any): void {
    const dt = this.parse(value);
    this.setForm(dt);
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  registerOnValidatorChange(fn: () => void): void {
    this.onValidatorChange = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
    this.applyDisabledState();
  }

  validate(control: AbstractControl): ValidationErrors | null {
    this.lastParentControl = control;
    const errors: ValidationErrors = {};
    const dateCtrl = this.form.get('date');
    const timeCtrl = this.form.get('time');
    if (!this.timeOnly && dateCtrl?.errors) Object.assign(errors, dateCtrl.errors);
    if (!this.dateOnly && timeCtrl?.errors) Object.assign(errors, timeCtrl.errors);
    for (const fn of this.validators) {
      const result = fn(control);
      if (result) Object.assign(errors, result);
    }
    return Object.keys(errors).length ? errors : null;
  }

  showInvalidState(): boolean {
    const parent = this.lastParentControl;
    if (parent && parent.invalid && (parent.touched || parent.dirty)) return true;
    return this.form.invalid && (this.form.touched || this.form.dirty);
  }

  errorMessage(): string | null {
    const parent = this.lastParentControl;
    const interactive = parent ? parent.touched || parent.dirty : this.form.touched || this.form.dirty;
    if (!interactive) return null;
    const errors: ValidationErrors = {
      ...(this.form.get('date')?.errors ?? {}),
      ...(this.form.get('time')?.errors ?? {}),
      ...(parent?.errors ?? {}),
    };
    if (errors.matDatepickerParse) return 'Invalid date format';
    if (errors.matDatepickerMin) return 'Date is before the allowed minimum';
    if (errors.matDatepickerMax) return 'Date is after the allowed maximum';
    if (errors.dateTimePast) return 'Date must be in the future';
    if (errors.required) return 'Required';
    const first = Object.keys(errors)[0];
    return first ? `Invalid: ${first}` : null;
  }

  private applyInitialValue(): void {
    if (this.defaultDate) {
      this.setForm(this.parse(this.defaultDate));
      this.emit();
    }
  }

  private applyDisabledState(): void {
    if (this.disabled) {
      this.form.disable({emitEvent: false});
    } else {
      this.form.enable({emitEvent: false});
    }
  }

  private parse(value: any): DateTime | null {
    if (value === null || value === undefined || value === '') return null;
    if (value instanceof DateTime) {
      if (!value.isValid) {
        throw new Error(this.formatError(`invalid Luxon DateTime (${value.invalidReason ?? 'unknown'})`));
      }
      return value;
    }
    if (value instanceof Date) {
      const dt = DateTime.fromJSDate(value);
      if (!dt.isValid) throw new Error(this.formatError('invalid JavaScript Date'));
      return dt;
    }
    if (typeof value === 'string') {
      if (this.timeOnly) {
        for (const fmt of ['HH:mm:ss', 'HH:mm']) {
          const timeDt = DateTime.fromFormat(value, fmt);
          if (timeDt.isValid) return timeDt;
        }
        throw new Error(this.formatError(`unrecognised string "${value}"`));
      }
      let dt = DateTime.fromISO(value);
      if (!dt.isValid) dt = DateTime.fromSQL(value);
      if (!dt.isValid) throw new Error(this.formatError(`unrecognised string "${value}"`));
      return dt;
    }
    throw new Error(this.formatError(`unsupported type ${typeof value}`));
  }

  private formatError(detail: string): string {
    const expected = this.timeOnly
      ? '"HH:mm" or "HH:mm:ss"'
      : this.dateOnly
        ? '"YYYY-MM-DD", ISO datetime, SQL datetime, Date, or Luxon DateTime'
        : 'ISO datetime ("YYYY-MM-DDTHH:mm:ss"), SQL datetime ("YYYY-MM-DD HH:mm:ss"), "YYYY-MM-DD", Date, or Luxon DateTime';
    return `app-datetime-picker-v2: ${detail}. Expected ${expected}.`;
  }

  private setForm(dt: DateTime | null): void {
    this.form.setValue(
      {
        date: dt,
        time: dt,
      },
      {emitEvent: false},
    );
  }

  private emit(): void {
    if (!this.initialized) return;
    const date: DateTime | null = this.form.get('date').value;
    const time: DateTime | null = this.form.get('time').value;
    if (this.timeOnly) {
      if (!time || !time.isValid) {
        this.onChange(null);
        this.onTouched();
        return;
      }
      this.onChange(time.toFormat('HH:mm'));
      this.onTouched();
      return;
    }
    if (!date || !date.isValid) {
      this.onChange(null);
      this.onTouched();
      return;
    }
    if (this.dateOnly) {
      this.onChange(date.toFormat('yyyy-MM-dd'));
      this.onTouched();
      return;
    }
    if (!time || !time.isValid) {
      this.onChange(null);
      this.onTouched();
      return;
    }
    const merged = date.set({hour: time.hour, minute: time.minute, second: 0, millisecond: 0});
    this.onChange(merged.toFormat("yyyy-MM-dd'T'HH:mm:00"));
    this.onTouched();
  }

  validateDateKeypress(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.metaKey) return true;
    const input = event.target as HTMLInputElement;
    const key = event.key;
    const value = input.value || '';
    const cursorAtEnd = (input.selectionStart ?? value.length) === value.length;

    if (/^\d$/.test(key)) {
      if (cursorAtEnd) {
        const digit = parseInt(key, 10);
        const position = value.length === 2 ? 3 : value.length === 5 ? 6 : value.length;
        if (position === 0 && digit > 3) return false;
        if (position === 1) {
          if (value[0] === '0' && digit === 0) return false;
          if (value[0] === '3' && digit > 1) return false;
        }
        if (position === 3 && digit > 1) return false;
        if (position === 4) {
          if (value[3] === '0' && digit === 0) return false;
          if (value[3] === '1' && digit > 2) return false;
        }
        if (position === 6 && digit !== 1 && digit !== 2) return false;
        if (value.length === 2 || value.length === 5) {
          input.value = value + '-';
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }
      return input.value.length < 10;
    }
    if (key === '-') {
      return cursorAtEnd && (value.length === 2 || value.length === 5);
    }
    return false;
  }

  validateTimeKeypress(event: KeyboardEvent): boolean {
    if (event.ctrlKey || event.metaKey) return true;
    const input = event.target as HTMLInputElement;
    const key = event.key;
    const value = input.value || '';
    const cursorAtEnd = (input.selectionStart ?? value.length) === value.length;

    if (/^\d$/.test(key)) {
      if (cursorAtEnd) {
        const digit = parseInt(key, 10);
        const position = value.length === 2 ? 3 : value.length;
        if (position === 0 && digit > 2) return false;
        if (position === 1 && value[0] === '2' && digit > 3) return false;
        if (position === 3 && digit > 5) return false;
        if (value.length === 2) {
          input.value = value + ':';
          input.setSelectionRange(input.value.length, input.value.length);
        }
      }
      return input.value.length < 5;
    }
    if (key === ':') {
      return cursorAtEnd && value.length === 2;
    }
    return false;
  }
}
